import React, { useState } from "react";
import { Form, Select, Card, Row, Col, Button, message, Modal, Spin, Radio } from "antd";
import { connect } from "react-redux";
import { getUserList } from "redux/selectors";
import { CALL_API } from "common/API";
import { ExclamationCircleOutlined, CloseOutlined, CheckCircleOutlined } from "@ant-design/icons";
import styled from "styled-components";
import dayjs from "dayjs";
import { borderRadius } from "@mui/system";

const { Option } = Select;

const StyledModal = styled(Modal)`
  .modal-header-icon {
    font-size: 48px;
    color: red;
    display: block;
    margin: 0 auto 16px;
  }
  .modal-title {
    text-align: center;
    font-size: 18px;
    margin-bottom: 24px;
  }
  .created-text {
    font-size: 1.1rem;
    font-weight: bold;
    margin: 32px 0 24px 0;
    text-align: center;
  }
  // Center modal content
  .ant-modal-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 400px;
    min-height: 220px;
  }
`;

function ChangeManager({ userList }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [pendingModalVisible, setPendingModalVisible] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [pendingTimesheets, setPendingTimesheets] = useState([]);
  const [pendingBillings, setPendingBillings] = useState([]);
  const [changeType, setChangeType] = useState("partner"); // "partner" or "personIncharge"

  const formatWeekRange = (weekStr) => {
    const [start, end] = weekStr.split("-");
    const fmt = (s) => dayjs(s, "YYYYMMMDD").format("DD MMM YYYY");
    return `${fmt(start)} – ${fmt(end)}`;
  };

  const handleSubmit = async () => {
    setLoading(true); // Ensure spinner starts
    try {
      const { partnerFrom, partnerTo, personInchargeFrom, personInchargeTo } =
        await form.validateFields();

      // Manual required validation
      if (changeType === "partner" && !partnerTo) {
        message.error("Partner To is required");
        setLoading(false);
        return;
      }
      if (changeType === "personIncharge" && !personInchargeTo) {
        message.error("Person Incharge To is required");
        setLoading(false);
        return;
      }

      // Build payload and query string based on changeType
      let pendingsPayload = {};
      let engagementPayload = {};
      let billingOrTimesheetPayload = {};
      let pendingsQS = "";
      let engagementQS = "";
      let billingOrTimesheetQS = "";

      if (changeType === "partner") {
        pendingsPayload = {
          oldPartner: partnerFrom,
          newPartner: partnerTo,
        };
        engagementPayload = { oldPartner: partnerFrom, newPartner: partnerTo };
        billingOrTimesheetPayload = { oldPartner: partnerFrom, newPartner: partnerTo };
        pendingsQS = `oldPartner=${partnerFrom || ""}&newPartner=${partnerTo || ""}`;
        engagementQS = pendingsQS;
        billingOrTimesheetQS = pendingsQS;
      } else if (changeType === "personIncharge") {
        pendingsPayload = {
          oldPersonIncharge: personInchargeFrom,
          newPersonIncharge: personInchargeTo,
        };
        engagementPayload = { oldPersonIncharge: personInchargeFrom, newPersonIncharge: personInchargeTo };
        billingOrTimesheetPayload = { oldPersonIncharge: personInchargeFrom, newPersonIncharge: personInchargeTo };
        pendingsQS = `oldPersonIncharge=${personInchargeFrom || ""}&newPersonIncharge=${personInchargeTo || ""}`;
        engagementQS = pendingsQS;
        billingOrTimesheetQS = pendingsQS;
      }

      const pendResp = await CALL_API(
        `insyts/engagement/pendingdatas?${pendingsQS}`,
        "patch",
        null
      );
      if (pendResp?.code !== "SUCCESS") {
        console.error("Pendings API error:", pendResp);
        message.error("Failed to check pending items.");
        setLoading(false);
        return;
      }

      const tsData = pendResp.timeSheetData || [];
      const billData = pendResp.billingData || [];

      if (tsData.length || billData.length) {
        setPendingTimesheets(
          tsData?.map((item) => ({
            employeeName: item.employeeName,
            week: formatWeekRange(item.week),
          }))
        );
        setPendingBillings(
          billData?.map((b) => ({
            billingid: b.billingid,
            billingoffice: b.billingoffice,
            contactperson: b.contactperson,
          }))
        );
        setPendingModalVisible(true);
        setLoading(false);
        return;
      }

      if (tsData.length === 0 && billData.length === 0) {
        message.success("No pending items. Updating managers…");
      }

      // Engagement API
      const engagementResp = await CALL_API(
        `insyts/engagement/engagement?${engagementQS}`,
        "patch",
        null
      );
      if (engagementResp?.code !== "SUCCESS") {
        message.error("Failed to update engagement.");
        setLoading(false);
        return;
      }

      // Billing or Timesheet API based on changeType
      if (changeType === "partner") {
        const billingResp = await CALL_API(
          `insyts/engagement/billing?${billingOrTimesheetQS}`,
          "patch",
          null
        );
        if (billingResp?.code !== "SUCCESS") {
          message.error("Failed to update billing.");
          setLoading(false);
          return;
        }
      } else if (changeType === "personIncharge") {
        const timesheetResp = await CALL_API(
          `insyts/engagement/timesheet?${billingOrTimesheetQS}`,
          "patch",
          null
        );
        if (timesheetResp?.code !== "SUCCESS") {
          message.error("Failed to update timesheet.");
          setLoading(false);
          return;
        }
      }

      setIsModalVisible(true);
      form.resetFields();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false); // Ensure spinner stops
    }
  };

  const handleOkClick = () => {
    setConfirmModalVisible(true);
  };

  const handleConfirmOk = async () => {
    setConfirmModalVisible(false);
    setLoading(true); // Ensure spinner starts
    try {
      const { partnerFrom, partnerTo, personInchargeFrom, personInchargeTo } =
        await form.validateFields();

      // Manual required validation
      if (changeType === "partner" && !partnerTo) {
        message.error("Partner To is required");
        setLoading(false);
        return;
      }
      if (changeType === "personIncharge" && !personInchargeTo) {
        message.error("Person Incharge To is required");
        setLoading(false);
        return;
      }

      // Build payload and query string based on changeType
      let engagementQS = "";
      let billingOrTimesheetQS = "";

      if (changeType === "partner") {
        engagementQS = `oldPartner=${partnerFrom || ""}&newPartner=${partnerTo || ""}`;
        billingOrTimesheetQS = engagementQS;
      } else if (changeType === "personIncharge") {
        engagementQS = `oldPersonIncharge=${personInchargeFrom || ""}&newPersonIncharge=${personInchargeTo || ""}`;
        billingOrTimesheetQS = engagementQS;
      }

      // Engagement API
      const engagementResp = await CALL_API(
        `insyts/engagement/engagement?${engagementQS}`,
        "patch",
        null
      );
      if (engagementResp?.code !== "SUCCESS") {
        message.error("Failed to update engagement.");
        setLoading(false);
        return;
      }

      // Billing or Timesheet API based on changeType
      if (changeType === "partner") {
        const billingResp = await CALL_API(
          `insyts/engagement/billing?${billingOrTimesheetQS}`,
          "patch",
          null
        );
        if (billingResp?.code !== "SUCCESS") {
          message.error("Failed to update billing.");
          setLoading(false);
          return;
        }
      } else if (changeType === "personIncharge") {
        const timesheetResp = await CALL_API(
          `insyts/engagement/timesheet?${billingOrTimesheetQS}`,
          "patch",
          null
        );
        if (timesheetResp?.code !== "SUCCESS") {
          message.error("Failed to update timesheet.");
          setLoading(false);
          return;
        }
      }

      setPendingModalVisible(false);
      form.resetFields();
      setIsModalVisible(true);
    } catch (err) {
      console.error(err);
      message.error(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false); // Ensure spinner stops
    }
  };

  const searchable = (name) => ({
    showSearch: true,
    allowClear: true,
    optionFilterProp: "children",
    filterOption: (inp, opt) =>
      opt.children.toLowerCase().includes(inp.toLowerCase()),
    filterSort: (a, b) =>
      a.children.toLowerCase().localeCompare(b.children.toLowerCase()),
    onClear: () => form.setFieldsValue({ [name]: undefined }),
    onChange: (val) => form.setFieldsValue({ [name]: val }),
  });

  // Helper to get employee names for modal message
  const getFromToNames = () => {
    let fromId, toId;
    if (changeType === "partner") {
      fromId = form.getFieldValue("partnerFrom");
      toId = form.getFieldValue("partnerTo");
    } else {
      fromId = form.getFieldValue("personInchargeFrom");
      toId = form.getFieldValue("personInchargeTo");
    }
    const fromUser = userList?.find(u => u.userid === fromId);
    const toUser = userList?.find(u => u.userid === toId);
    return {
      from: fromUser ? fromUser.fullname : "",
      to: toUser ? toUser.fullname : ""
    };
  };

  // Modified handleOkClick: directly start API calls and show loader
  const handlePendingOk = async () => {
    setLoading(true);
    try {
      const { partnerFrom, partnerTo, personInchargeFrom, personInchargeTo } =
        await form.validateFields();

      // Build payload and query string based on changeType
      let engagementQS = "";
      let billingOrTimesheetQS = "";

      if (changeType === "partner") {
        engagementQS = `oldPartner=${partnerFrom || ""}&newPartner=${partnerTo || ""}`;
        billingOrTimesheetQS = engagementQS;
      } else if (changeType === "personIncharge") {
        engagementQS = `oldPersonIncharge=${personInchargeFrom || ""}&newPersonIncharge=${personInchargeTo || ""}`;
        billingOrTimesheetQS = engagementQS;
      }

      // Engagement API
      const engagementResp = await CALL_API(
        `insyts/engagement/engagement?${engagementQS}`,
        "patch",
        null
      );
      if (engagementResp?.code !== "SUCCESS") {
        message.error("Failed to update engagement.");
        setLoading(false);
        return;
      }

      // Billing or Timesheet API based on changeType
      if (changeType === "partner") {
        const billingResp = await CALL_API(
          `insyts/engagement/billing?${billingOrTimesheetQS}`,
          "patch",
          null
        );
        if (billingResp?.code !== "SUCCESS") {
          message.error("Failed to update billing.");
          setLoading(false);
          return;
        }
      } else if (changeType === "personIncharge") {
        const timesheetResp = await CALL_API(
          `insyts/engagement/timesheet?${billingOrTimesheetQS}`,
          "patch",
          null
        );
        if (timesheetResp?.code !== "SUCCESS") {
          message.error("Failed to update timesheet.");
          setLoading(false);
          return;
        }
      }

      setPendingModalVisible(false);
      form.resetFields();
      setIsModalVisible(true);
    } catch (err) {
      console.error(err);
      message.error(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Global page spinner overlay */}
      {loading && (
        <div
          style={{
            position: "fixed",
            zIndex: 2000,
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(255,255,255,0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column"
          }}
        >
          <Spin size="large" />
          <div style={{ marginTop: 16, fontSize: 18, color: "#333" }}>
            Bulk Loading...
          </div>
        </div>
      )}

      <Card title="Change Manager">
        {/* Radio buttons for change type */}
        <Row style={{ marginBottom: 24 }}>
          <Col>
            <Radio.Group
              value={changeType}
              onChange={e => {
                setChangeType(e.target.value);
                if (e.target.value === "partner") {
                  form.resetFields(["personInchargeFrom", "personInchargeTo"]);
                } else if (e.target.value === "personIncharge") {
                  form.resetFields(["partnerFrom", "partnerTo"]);
                }
              }}
            >
              <Radio value="partner">Partner</Radio>
              <Radio value="personIncharge">Person Incharge</Radio>
            </Radio.Group>
          </Col>
        </Row>
        <Form form={form} layout="vertical">
          {/* Partner From / To */}
          {changeType === "partner" && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label={("Partner From")}
                  name="partnerFrom"
                  rules={[
                    { required: true, message: "Partner From is required" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (
                          !value ||
                          getFieldValue("partnerTo") === undefined ||
                          value !== getFieldValue("partnerTo")
                        ) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Partner To and From cannot be the same")
                        );
                      },
                    }),
                  ]}
                >
                  <Select
                    {...searchable("partnerFrom")}
                    placeholder="Select Partner From"
                  >
                    {userList?.map((u) => (
                      <Option key={u.userid} value={u.userid}>
                        {u.fullname}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={("Partner To")}
                  name="partnerTo"
                  rules={[
                    { required: true, message: "Partner To is required" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (
                          !value ||
                          getFieldValue("partnerFrom") === undefined ||
                          value !== getFieldValue("partnerFrom")
                        ) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Partner From and To cannot be the same")
                        );
                      },
                    }),
                  ]}
                >
                  <Select
                    {...searchable("partnerTo")}
                    placeholder="Select Partner To"
                  >
                    {userList
                      .filter((u) => u.statusid === "1")
                      ?.map((u) => (
                        <Option key={u.userid} value={u.userid}>
                          {u.fullname}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          )}
          {/* Person In-Charge From / To */}
          {changeType === "personIncharge" && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label={("Person Incharge From")}
                  name="personInchargeFrom"
                  rules={[
                    { required: true, message: "Person Incharge From is required" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (
                          !value ||
                          getFieldValue("personInchargeTo") === undefined ||
                          value !== getFieldValue("personInchargeTo")
                        ) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Person Incharge From and To cannot be the same")
                        );
                      },
                    }),
                  ]}
                >
                  <Select
                    {...searchable("personInchargeFrom")}
                    placeholder="Select Person Incharge From"
                  >
                    {userList?.map((u) => (
                      <Option key={u.userid} value={u.userid}>
                        {u.fullname}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={("Person Incharge To")}
                  name="personInchargeTo"
                  rules={[
                    { required: true, message: "Person Incharge To is required" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (
                          !value ||
                          getFieldValue("personInchargeFrom") === undefined ||
                          value !== getFieldValue("personInchargeFrom")
                        ) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Person Incharge To and From cannot be the same")
                        );
                      },
                    }),
                  ]}
                >
                  <Select
                    {...searchable("personInchargeTo")}
                    placeholder="Select Person Incharge To"
                  >
                    {userList
                      .filter((u) => u.statusid === "1")
                      ?.map((u) => (
                        <Option key={u.userid} value={u.userid}>
                          {u.fullname}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          )}
          {/* Submit */}
          <Row justify="end" style={{ marginTop: 16 }}>
            <Button type="primary" onClick={handleSubmit} >
              Assign
            </Button>
          </Row>
        </Form>
      </Card>

      {/* Success Modal */}
      <StyledModal
        open={isModalVisible}
        footer={null}
        closable={false}
        centered
        bodyStyle={{ textAlign: "center" }}
        width={500}
      >
        <CheckCircleOutlined style={{ fontSize: 64, color: "#52c41a", marginBottom: 16 }} />
        <p className="created-text">Assigned Successfully!</p>
        <CloseOutlined
          style={{ position: "absolute", top: 16, right: 16, cursor: "pointer" }}
          onClick={() => {
            setIsModalVisible(false);
            form.resetFields(); // Reset form fields
          }}
        />
      </StyledModal>

      {/* Pending Items Modal */}
      <StyledModal
        open={pendingModalVisible}
        footer={null}
        closable={false}
        maskClosable={!loading}
        bodyStyle={{ textAlign: "center" }}
      >
        {(pendingTimesheets.length > 0 || pendingBillings.length > 0) ? (
          (() => {
            const { from, to } = getFromToNames();
            return (
              <>
                <ExclamationCircleOutlined className="modal-header-icon" />
                <p style={{ fontSize: "16px" }}>
                  Pending items are detected from the employee <b>{from}</b>.
                  If you change to {to}, all the items will move to <b>{to}</b>.
                  If you agree, then click OK.
                </p>
                <Button
                  type="primary"
                  onClick={handlePendingOk}
                  style={{ marginTop: 16 }}
                  disabled={loading}
                >
                  OK
                </Button>
                <CloseOutlined
                  style={{ position: "absolute", top: 16, right: 16, cursor: "pointer" }}
                  onClick={() => !loading && setPendingModalVisible(false)}
                />
              </>
            );
          })()
        ) : (
          <>
          </>
        )}
      </StyledModal>
    </>
  );
}

const mapStateToProps = (state) => ({
  userList: getUserList(state),
});

export default connect(mapStateToProps)(ChangeManager);
